'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { MeseroFrame } from '@/modules/mesero/components/MeseroFrame'
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter, DrawerClose } from '@/components/ui/drawer'
import { useMesero } from '@/modules/mesero/MeseroProvider'
import type { OrderItem, Product } from '@/lib/pos-types'
import { cn, createClientId, formatPrice } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { RefreshCcwIcon } from 'lucide-react'

export default function ComandaPage() {
  const navigate = useNavigate()
  const params = useParams()
  const tableId = String(params.tableId ?? '')
  const { apiEnabled, apiOnline, currentUser, tables, products, categories, getOrderForTableId, saveOrderDraft, sendOrderToKitchen } = useMesero()

  const table = useMemo(() => tables.find((t) => t.id === tableId) ?? null, [tableId, tables])
  const existingOrder = useMemo(() => {
    if (!tableId) return null
    if (table?.status === 'available' && !table.currentOrderId) return null
    return getOrderForTableId(tableId)
  }, [getOrderForTableId, table, tableId])

  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('all')
  const [productSearch, setProductSearch] = useState('')
  const [userEdited, setUserEdited] = useState(false)
  const [orderItems, setOrderItems] = useState<OrderItem[]>(() => existingOrder?.items ?? [])

  const [editingNoteId, setEditingNoteId] = useState<string | null>(null)
  const [noteText, setNoteText] = useState('')

  const hadActiveOrderRef = useRef<boolean>(false)

  useEffect(() => {
    if (existingOrder && existingOrder.status !== 'closed' && existingOrder.status !== 'paid') {
      hadActiveOrderRef.current = true
    }
  }, [existingOrder])

  useEffect(() => {
    const isTableClosed = (table?.status === 'available' && !table?.currentOrderId) || (existingOrder && (existingOrder.status === 'closed' || existingOrder.status === 'paid'))
    if (isTableClosed && hadActiveOrderRef.current) {
      toast.info('La mesa fue cerrada por un administrador', {
        description: table ? `Mesa ${table.number} ya está libre` : 'Comanda cerrada',
      })
      navigate('/mesero/mesas', { replace: true })
    }
  }, [table, existingOrder, navigate])

  // Ref para evitar sincronizar en el montaje (ya se inicializó el estado)
  const mountedRef = useRef(false)

  // Sincroniza `orderItems` con los datos del servidor SOLO si el usuario no ha editado.
  // Esto permite que el polling actualice los datos sin romper las ediciones locales.
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true
      return // ya inicializado en useState
    }
    if (!userEdited) {
      setOrderItems(existingOrder?.items ?? [])
    }
  }, [existingOrder]) // eslint-disable-line react-hooks/exhaustive-deps

  // `effectiveItems` siempre usa el estado local `orderItems` (inmune al polling)
  const effectiveItems = orderItems
  const pendingItems = useMemo(() => effectiveItems.filter((i) => i.status === 'pending'), [effectiveItems])
  const isLocked = existingOrder?.status === 'pending-payment' || existingOrder?.status === 'closed'

  const computeEffective = useMemo(() => {
    const now = new Date()
    const day = now.getDay() // 0=Dom..6=Sáb
    const toMinutes = (hhmm: string) => {
      const [h, m] = hhmm.split(':').map((n) => Number(n))
      if (!Number.isFinite(h) || !Number.isFinite(m)) return null
      return h * 60 + m
    }
    const inRange = (start: string, end: string) => {
      const s = toMinutes(start)
      const e = toMinutes(end)
      if (s === null || e === null) return false
      const cur = now.getHours() * 60 + now.getMinutes()
      if (s <= e) return cur >= s && cur <= e
      return cur >= s || cur <= e
    }
    const effectiveAvailable = (p: Product) => {
      if (!p.available) return false
      if (!p.availabilityRules || p.availabilityRules.length === 0) return p.available
      return p.availabilityRules.some((r) => r.days?.includes(day) && inRange(r.start, r.end))
    }
    const effectivePrice = (p: Product) => {
      if (!p.priceRules || p.priceRules.length === 0) return p.price
      const match = p.priceRules.find((r) => r.days?.includes(day) && inRange(r.start, r.end))
      return match ? match.price : p.price
    }
    const map = new Map<string, { available: boolean; price: number }>()
    products.forEach((p) => {
      map.set(p.id, { available: effectiveAvailable(p), price: effectivePrice(p) })
    })
    return map
  }, [products])

  const filteredProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase()
    // Filtrar productos por disponibilidad (horario)
    const available = products.filter((p) => {
      const eff = computeEffective.get(p.id)?.available ?? p.available
      return eff
    })
    const byCategory = selectedCategoryId === 'all' ? available : available.filter((p) => p.categoryId === selectedCategoryId)
    if (!q) return byCategory
    return available.filter((p) => p.name.toLowerCase().includes(q) || (p.description ?? '').toLowerCase().includes(q))
  }, [products, selectedCategoryId, computeEffective, productSearch])

  const colorsByCategoryId = useMemo(() => {
    const palette = ['#a78bfa', '#4d9fff', '#00e5a0', '#f5a623']
    const map = new Map<string, string>()
    categories
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .forEach((c, idx) => {
        map.set(c.id, palette[idx % palette.length])
      })
    return map
  }, [categories])

  const getQuantityForProduct = (productId: string) => {
    return pendingItems.filter((i) => i.productId === productId).reduce((sum, i) => sum + i.quantity, 0)
  }

  const upsertItemForProduct = (product: Product, delta: number) => {
    if (isLocked) {
      toast.error('La mesa está en cobro o cerrada')
      return
    }

    // Informar stock disponible
    if (!product.stockAvailable) {
      toast.warning(`Atención: Sin stock suficiente para ${product.name} (Venta permitida)`)
    }

    const base = userEdited ? orderItems : effectiveItems
    const existing = base.find((i) => i.productId === product.id && i.status === 'pending')
    setUserEdited(true)

    if (existing) {
      // Informar límite de porciones al incrementar
      if (delta > 0 && product.stockPortions !== null) {
        const pendingQty = base
          .filter((i) => i.productId === product.id && i.status === 'pending')
          .reduce((sum, i) => sum + i.quantity, 0)
        if (pendingQty >= product.stockPortions) {
          toast.warning(`Atención: Quedan ${product.stockPortions} porción(es) de ${product.name} (Venta permitida)`)
        }
      }
      const updated = base
        .map((i) => (i.id === existing.id ? { ...i, quantity: i.quantity + delta } : i))
        .filter((i) => i.quantity > 0)
      setOrderItems(updated)
      if (table) {
        saveOrderDraft(table.id, updated)
      }
      return
    }

    if (delta <= 0) return

    // Informar límite al agregar la primera porción
    if (product.stockPortions !== null && product.stockPortions < 1) {
      toast.warning(`Atención: Sin stock para ${product.name} (Venta permitida)`)
    }

    const eff = computeEffective.get(product.id)
    const unitPrice = eff ? eff.price : product.price
    const newItem: OrderItem = {
      id: createClientId('item'),
      productId: product.id,
      productName: product.name,
      quantity: 1,
      unitPrice,
      status: 'pending',
      addedAt: new Date(),
    }
    const updated = [...base, newItem]
    setOrderItems(updated)
    if (table) {
      saveOrderDraft(table.id, updated)
    }
  }

  const handleRemoveEditingItem = (itemId: string) => {
    setUserEdited(true)
    const nextItems = orderItems.filter((i) => i.id !== itemId)
    setOrderItems(nextItems)
    if (table) {
      const eff = nextItems.filter((i) => i.status !== 'cancelled')
      saveOrderDraft(table.id, eff)
    }
    setEditingNoteId(null)
    setNoteText('')
    toast.success('Producto eliminado de la comanda')
  }

  const openNoteForProduct = (product: Product) => {
    if (isLocked) {
      toast.error('La mesa está en cobro o cerrada')
      return
    }
    const existing = orderItems.find((i) => i.productId === product.id && i.status === 'pending')
    if (existing) {
      setEditingNoteId(existing.id)
      setNoteText(existing.notes ?? '')
      return
    }

    const eff = computeEffective.get(product.id)
    const unitPrice = eff ? eff.price : product.price
    const newItem: OrderItem = {
      id: createClientId('item'),
      productId: product.id,
      productName: product.name,
      quantity: 1,
      unitPrice,
      status: 'pending',
      addedAt: new Date(),
    }
    setUserEdited(true)
    setOrderItems([...orderItems, newItem])
    setEditingNoteId(newItem.id)
    setNoteText('')
  }

  const saveCurrentNote = (text: string) => {
    if (!editingNoteId) return
    const trimmed = text.trim()
    if (trimmed.length > 120) {
      toast.error('La nota no puede superar 120 caracteres')
      return
    }
    setUserEdited(true)
    const nextItems = orderItems.map((i) => (i.id === editingNoteId ? { ...i, notes: trimmed || undefined } : i))
    setOrderItems(nextItems)
    if (table) {
      const eff = nextItems.filter((i) => i.status !== 'cancelled')
      saveOrderDraft(table.id, eff)
    }
  }

  const saveNote = () => {
    if (!editingNoteId) return
    saveCurrentNote(noteText)
    setEditingNoteId(null)
    setNoteText('')
  }

  const { countProducts, total } = useMemo(() => {
    const subtotal = pendingItems.reduce((sum, i) => sum + i.quantity * i.unitPrice, 0)
    const tax = Math.round(subtotal * 0.19)
    const totalValue = subtotal + tax
    return { countProducts: pendingItems.reduce((sum, i) => sum + i.quantity, 0), total: totalValue }
  }, [pendingItems])

  const canSend = !isLocked && pendingItems.length > 0

  const handleSend = () => {
    if (!table) return
    if (pendingItems.length === 0) {
      toast.error('Agrega al menos un producto')
      return
    }
    sendOrderToKitchen(table.id, effectiveItems)
    if (apiEnabled && apiOnline) {
      toast.success('Comanda enviada a cocina', { description: `Mesa ${table.number} (${table.zone}) · ${countProducts} productos` })
    } else {
      toast.warning('Sin conexión', { description: `Comanda guardada y pendiente de sincronización · Mesa ${table.number} (${table.zone})` })
    }
    setUserEdited(false)
    navigate('/mesero/mesas')
  }

  const handleSaveDraft = () => {
    if (!table) return
    if (!userEdited) return
    saveOrderDraft(table.id, effectiveItems)
    if (effectiveItems.length > 0) {
      toast.success('Pedido guardado', { description: `Mesa ${table.number} (${table.zone}) · ${countProducts} productos` })
    } else {
      toast.success('Comanda vaciada', { description: `Mesa ${table.number} (${table.zone})` })
    }
    setUserEdited(false)
  }

  if (!table) {
    return (
      <MeseroFrame>
        <div className="flex-1 flex flex-col items-center justify-center text-[#9a9aa8]">Mesa no encontrada</div>
      </MeseroFrame>
    )
  }

  return (
    <MeseroFrame>
      <div className="flex-1 overflow-hidden flex flex-col bg-[#0f0f11]">
        <div className="px-5 pt-3 pb-2 flex items-center gap-3 shrink-0">
          <button
            type="button"
            onClick={() => navigate('/mesero/mesas')}
            className="w-9 h-9 rounded-full bg-[#1e1e23] border border-white/10 flex items-center justify-center"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="#f0f0f0" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <div className="flex-1">
            <div className="text-[18px] font-semibold text-[#f0f0f0] leading-tight flex items-baseline gap-1.5">
              <span>Mesa {table.number}</span>
              <span className="text-[12px] font-normal text-[#9a9aa8]">({table.zone})</span>
            </div>
            <div className="text-[11px] text-[#9a9aa8] mt-0.5">Mesero: {currentUser.name}</div>
          </div>
          {existingOrder?.diners && (
            <div className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-[rgba(0,229,160,0.12)] text-[#00e5a0] border border-[rgba(0,229,160,0.25)]">
              {existingOrder.diners} personas
            </div>
          )}
           <button
            type="button"
            onClick={() => setProductSearch("")}
            className="w-9 h-9 rounded-full bg-[#1e1e23] border border-white/10 flex items-center justify-center"
          >
            <svg
              viewBox="0 0 24 24"
              className="w-4 h-4"
              fill="none"
              stroke="#f0f0f0"
              strokeWidth="2"
            >
              <RefreshCcwIcon className="size-4" />
            </svg>
          </button>
        </div>

        <div className="px-5 pb-2 shrink-0">
          <Input
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
            placeholder="Buscar producto..."
            onFocus={()=> setProductSearch("")}
            className="bg-[#1e1e23] border-white/10 text-[#f0f0f0] placeholder:text-[#5a5a6a]"
          />
        </div>

        <div className="flex gap-1.5 px-5 pb-3 overflow-x-auto shrink-0">
          <CatPill label="Todo" active={selectedCategoryId === 'all'} onClick={() => setSelectedCategoryId('all')} />
          {categories
            .slice()
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .map((c) => (
              <CatPill key={c.id} label={c.name} active={selectedCategoryId === c.id} onClick={() => setSelectedCategoryId(c.id)} />
            ))}
        </div>

        <div className="flex-1 overflow-y-auto px-5">
          <div className="divide-y divide-white/10">
            {filteredProducts.map((p) => {
              const qty = getQuantityForProduct(p.id)
              const color = colorsByCategoryId.get(p.categoryId) ?? '#00e5a0'
              const pendingItem = pendingItems.find((i) => i.productId === p.id) ?? null
              const LOW_STOCK_THRESHOLD = 4
              const isLowStock =
                p.stockPortions !== null &&
                p.stockPortions > 0 &&
                p.stockPortions <= LOW_STOCK_THRESHOLD
              // Bloquear + si ya se alcanzó el límite de porciones pendientes
              const reachedPortionLimit =
                p.stockPortions !== null && qty >= p.stockPortions
              return (
                <div
                  key={p.id}
                  className="py-3.5 flex gap-3 cursor-pointer border-b border-white/5"
                  onClick={() => openNoteForProduct(p)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') openNoteForProduct(p)
                  }}
                >
                  <div className="w-1 rounded-full self-stretch shrink-0" style={{ background: color }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="text-[15px] font-medium text-[#f0f0f0] leading-snug flex-1">
                        {p.name}
                      </div>
                      {(() => {
                        const effectivePrice = computeEffective.get(p.id)?.price ?? p.price
                        if (effectivePrice === p.price) {
                          return <div className="font-mono text-[14px] font-semibold text-[#f0f0f0] shrink-0 pt-0.5">{formatPrice(p.price)}</div>
                        }
                        return (
                          <div className="flex flex-col items-end leading-none shrink-0 pt-0.5">
                            <div className="font-mono text-[14px] font-semibold text-[#00e5a0]">{formatPrice(effectivePrice)}</div>
                            <div className="font-mono text-[11px] text-[#9a9aa8] line-through mt-1">{formatPrice(p.price)}</div>
                          </div>
                        )
                      })()}
                    </div>

                    {p.description && <div className="text-[12px] text-[#9a9aa8] mt-1 leading-snug">{p.description}</div>}
                    {pendingItem?.notes && <div className="text-[12px] text-[#f5a623] mt-1 font-medium">📝 {pendingItem.notes}</div>}
                    {isLowStock && (
                      <div className="text-[11px] text-[#f5a623] font-semibold mt-1">
                        ⚠️ {p.stockPortions} disp.
                      </div>
                    )}

                    {/* Botones de acción y cantidad debajo del nombre del plato */}
                    <div className="flex items-center justify-between gap-1.5 mt-2 pt-1.5 border-t border-white/5" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => openNoteForProduct(p)}
                        className="text-[10px] text-[#9a9aa8] hover:text-[#00e5a0] flex items-center gap-1 transition-colors py-0.5 px-1.5 rounded bg-[#1e1e23] border border-white/10 shrink-0"
                      >
                        <span>📝 {pendingItem?.notes ? 'Nota' : '+ Nota'}</span>
                      </button>

                      <div className="flex items-center gap-1 bg-[#1e1e23] border border-white/10 rounded-md px-1 py-0.5 shrink-0">
                        <button
                          type="button"
                          className="w-5 h-5 rounded flex items-center justify-center text-[13px] font-bold border border-white/15 bg-[#26262d] text-[#f0f0f0] disabled:opacity-30 disabled:border-white/5"
                          onClick={() => upsertItemForProduct(p, -1)}
                          disabled={isLocked || qty === 0}
                        >
                          −
                        </button>
                        <div className="font-mono text-[12px] font-semibold text-[#f0f0f0] min-w-3 text-center">{qty}</div>
                        <button
                          type="button"
                          className={cn(
                            'w-5 h-5 rounded flex items-center justify-center text-[13px] font-bold border transition-colors',
                            isLocked || reachedPortionLimit
                              ? 'bg-[#26262d] border-white/10 text-[#5a5a6a] opacity-40'
                              : 'bg-[rgba(0,229,160,0.15)] border-[#00e5a0] text-[#00e5a0]',
                          )}
                          onClick={() => upsertItemForProduct(p, 1)}
                          disabled={isLocked || reachedPortionLimit}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <div className="mx-5 mt-3 rounded-2xl bg-[#1e1e23] border border-white/15 px-4 py-3 flex items-center gap-3 shrink-0">
          <div className="text-[13px] text-[#9a9aa8] flex-1">
            <strong className="text-[#f0f0f0] font-semibold">{countProducts}</strong> productos
          </div>
          <div className="font-mono text-[16px] font-semibold text-[#f0f0f0]">{formatPrice(total)}</div>
          <button
            type="button"
            onClick={() => {
              if (userEdited && effectiveItems.length > 0) {
                saveOrderDraft(table.id, effectiveItems)
                setUserEdited(false)
              }
              navigate(`/mesero/mesa/${table.id}/items`, { state: { items: effectiveItems } })
            }}
            className="ml-2 px-3 py-2 rounded-xl border border-white/10 bg-[#26262d] text-[#f0f0f0] text-[12px] font-medium"
          >
            Ver items
          </button>
        </div>

        <div className="px-5 pb-5 pt-3 shrink-0">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleSaveDraft}
              disabled={isLocked || !userEdited || effectiveItems.length === 0}
              className="flex-1 py-4 rounded-2xl border border-white/10 bg-[#26262d] text-[#f0f0f0] text-[15px] font-semibold disabled:text-[#5a5a6a]"
            >
              Guardar
            </button>
            <button
              type="button"
              onClick={handleSend}
              disabled={!canSend}
              className="flex-1 py-4 rounded-2xl bg-[#00e5a0] text-black text-[15px] font-semibold disabled:bg-[#26262d] disabled:text-[#5a5a6a]"
            >
              Enviar a cocina
            </button>
          </div>
        </div>
      </div>

      <Drawer
        open={Boolean(editingNoteId)}
        onOpenChange={(open) => {
          if (!open) {
            if (editingNoteId) {
              saveCurrentNote(noteText)
            }
            setEditingNoteId(null)
            setNoteText('')
          }
        }}
      >
        <DrawerContent className="bg-[#1e1e23] border-t border-white/10 text-[#f0f0f0]">
          <div className="mx-auto mt-4 h-1 w-10 rounded-full bg-white/15" />
          <DrawerHeader className="text-left px-5">
            <DrawerTitle className="text-[#f0f0f0] text-base font-semibold">Agregar nota</DrawerTitle>
            <DrawerDescription className="text-[#9a9aa8] text-xs">Instrucciones especiales para este producto</DrawerDescription>
          </DrawerHeader>
          <div className="px-5 pb-2">
            <Input
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              onBlur={() => {
                saveCurrentNote(noteText)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  saveNote()
                }
              }}
              placeholder="Ej: sin cebolla, término medio..."
              className="bg-[#26262d] border-white/10 text-[#f0f0f0] placeholder:text-[#5a5a6a]"
              maxLength={120}
            />
          </div>
          <DrawerFooter className="px-5 pb-8">
            <div className="flex gap-2">
              <DrawerClose asChild>
                <button
                  type="button"
                  onClick={saveNote}
                  className="flex-1 py-3 rounded-xl bg-[#00e5a0] text-black text-sm font-semibold"
                >
                  Listo
                </button>
              </DrawerClose>
            </div>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </MeseroFrame>
  )
}

function CatPill({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-4 py-2 rounded-full text-[12px] font-medium border whitespace-nowrap transition-colors',
        active ? 'bg-[rgba(0,229,160,0.12)] border-[#00e5a0] text-[#00e5a0]' : 'bg-[#1e1e23] border-white/10 text-[#9a9aa8]',
      )}
    >
      {label}
    </button>
  )
}
